// ============================================================
// TERROR — THE FAUXDEX
// The Codex's shadow twin. Every EXPERIMENTAL card in the game,
// on display and nothing more. Pure hypothesis: no card here is
// obtainable, playable, or rule-implemented — ever. When caP'n
// canonizes a card, it is MOVED out of experimentalCards.js into
// cards.js and leaves this hall for good (its blessing, its Mark).
//
// ✦ THE POCKET LAW: this file is the ONLY module in the game
// allowed to import experimentalCards.js. Read-only, always.
//
// Tabs:
//   ◆ Cards    — all 400+ experimental cards, filters, foil preview
//   ✦ Decks    — the hypothetical starter deck of every new line
//   ◈ Schools  — the 8 experimental Tome Schools and their laws
// ============================================================

import { useState, useMemo } from "react";
import {
  GM, OBSIDIAN_BG, OBSIDIAN_SLAB, OBSIDIAN_SLAB_LIT, OBSIDIAN_RECESS,
  OBSIDIAN_PANEL, facetTB,
  SHADOW_RAISED, SHADOW_RECESS, SHADOW_FLOAT, textGlow, TEXT_SET,
} from "./theme";
import { StarDust, NoiseOverlay, UnstableWorldBG } from "./TerrorUI";
import { useStageRect } from "./RoomBackdrop";
import { useModalKeys } from "./KeyboardNav";
import HoloFoil from "./HoloFoil";
import FauxArt from "./FauxArt"; // procedural provisional card faces — Terror style, no assets
import { SILHOUETTES, SILHOUETTE_FOR_LINE } from "./fauxSilhouettes";
import {
  ALL_EXPERIMENTAL_CARDS, EX_STARTER_DECKS, EXPERIMENTAL_TOME_SCHOOLS,
  getExImperaForBeing, getExUhltiForBeing,
} from "./experimentalCards";

// ── THE WING'S OWN LIGHT — violet, never the Codex's gold ────
const FX = {
  ink: "#8a5fd8",
  bright: "#c9b3ff",
  dim: "#5d3f96",
  wash: "rgba(74,47,124,0.22)",
};

// Class → display color (experimental Schools + the canon 4).
const CLASS_COLORS = {
  "Astrae": "#2f9bd8", "N'ihil": "#8b8b99", "O'mnia": "#c23b3b",
  "Avarice": "#c9a23a", "Solaea": "#e8c34a", "Lunaea": "#7c8bdb",
  "Dark Art": "#9a5fb0", "Arcane Art": "#8a5fd8", "Shadow Gambling": "#b06fd8",
  "Space": "#2f5bd8", "Phantom": "#4a9c69", "Time": "#c98a4b", "Dragon": "#c8a84b",
};
const classColor = (c) => CLASS_COLORS[c] || FX.ink;

// For supports (Tome/Impera/U'hlti) the school-ish word sits on
// different fields; one accessor so the filters read every card.
const classOf = (card) =>
  card.type === "Tome" ? card.ilk : (card.school || null);

const LEVEL_WORDS = { 1: "Beb", 2: "Champion", 3: "Lord" };

// ── PROVISIONAL ART (caP'n, 2026-09-03) ─────────────────────
// No experimental card has real asset art yet, so every card face is
// GENERATED — FauxArt.js draws the whole card in the canon frame
// language (engraved border, banner, halo portrait, parchment) and
// fauxSilhouettes.js supplies a bust silhouette per species line.
// Deterministic per card id. The day a card is canonized it gets real
// art via the CDN like every canon card; none of this ships past the
// Fauxdex.
const silhouetteFor = (card) => {
  const base = card.owner || card.id || "";
  let best = null, bestLen = -1;
  for (const [pref, key] of Object.entries(SILHOUETTE_FOR_LINE)) {
    if (base.startsWith(pref) && pref.length > bestLen) { best = key; bestLen = pref.length; }
  }
  return best ? (SILHOUETTES[best] || null) : null;
};
const artCardOf = (card) => ({ ...card, class: classOf(card) || card.type });

const TYPE_ICONS = {
  Escendant: "⬆", Tome: "📖", Impera: "✦", "U'hlti": "◈", Escling: "🌱", All: "",
};
const TYPE_OPTIONS = ["All", "Escendant", "Tome", "Impera", "U'hlti", "Escling"];
const TYPE_LABELS = { "U'hlti": "U'hltima" };
const CLASS_OPTIONS = ["All", ...Object.keys(CLASS_COLORS)];

// Foil preview — the wing's proof that Gold and Silver will land
// on experimental cards with the same consistency as canon. Only
// these two exist here on purpose (the rest come with canonization).
const FOILS = [
  { id: null,          label: "◇ Plain"  },
  { id: "holo_silver", label: "◈ Silver", color: "#cccccc" },
  { id: "holo_gold",   label: "✦ Gold",   color: "#d4a84b" },
];

// ── FAUXDEX KEYFRAMES — imperative injection (Material Law 6) ──
function ensureFauxdexKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById("fx-keyframes")) return;
  const el = document.createElement("style");
  el.id = "fx-keyframes";
  el.textContent = `
    @keyframes fx-glowSilver {
      0%,100% { box-shadow:0 0 6px #aaaaaa55, 0 0 12px #cccccc33, 0 0 0 1px #aaaaaa88; }
      50%     { box-shadow:0 0 14px #ddddddaa, 0 0 28px #cccccc66, 0 0 0 1px #eeeeeecc; }
    }
    @keyframes fx-glowGold {
      0%,100% { box-shadow:0 0 10px #d4a84baa, 0 0 22px #d4a84b55, 0 0 0 1px #d4a84bcc; }
      50%     { box-shadow:0 0 20px #ffd700cc, 0 0 40px #d4a84b88, 0 0 0 2px #ffd700; }
    }
    .fx-holo-silver { animation: fx-glowSilver 3s ease-in-out infinite; }
    .fx-holo-gold   { animation: fx-glowGold 2.5s ease-in-out infinite; }
  `;
  document.head.appendChild(el);
}
ensureFauxdexKeyframes();

// ─────────────────────────────────────────────────────────────
// CARD TILE — no CDN art exists for any experimental card yet,
// so the tile IS the card: a designed mini-face, not a broken img.
// ─────────────────────────────────────────────────────────────
function FauxTile({ card, foil, onClick }) {
  const cc = classColor(classOf(card));
  const glowClass = foil === "holo_gold" ? "fx-holo-gold"
    : foil === "holo_silver" ? "fx-holo-silver" : "";
  return (
    <div onClick={() => onClick(card)} style={{ width: 86, cursor: "pointer", position: "relative" }}>
      <div className={glowClass} style={{
        borderRadius: 6, overflow: "hidden",
        border: `1px solid ${cc}66`,
        borderTop: `2px solid ${cc}`,
        background: `linear-gradient(160deg, ${cc}1c, transparent 55%), ${OBSIDIAN_SLAB}`,
        aspectRatio: "2/3",
        display: "flex", flexDirection: "column",
        padding: 6, boxSizing: "border-box",
        position: "relative",
        boxShadow: SHADOW_RAISED,
      }}>
        <FauxArt card={artCardOf(card)} mode="tile" silhouette={silhouetteFor(card)} color={cc} species={card.species || null} />
        {foil && <HoloFoil tier={foil} radius={6} corners={false} style={{ zIndex: 1 }} />}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CARD DETAIL MODAL — full card face + the honest label.
// ─────────────────────────────────────────────────────────────
function FauxDetailModal({ card, onClose }) {
  const [foil, setFoil] = useState(null);
  const cc = classColor(classOf(card));
  const glowClass = foil === "holo_gold" ? "fx-holo-gold"
    : foil === "holo_silver" ? "fx-holo-silver" : "";
  useModalKeys(onClose);
  const impera = card.type === "Escendant" ? getExImperaForBeing(card.id) : [];
  const uhlti = card.type === "Escendant" && card.level === 3 ? getExUhltiForBeing(card.id) : null;
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(4,2,10,0.96)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 500, backdropFilter: "blur(8px)",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: "min(92vw, 340px)", maxHeight: "92vh", overflowY: "auto",
        background: OBSIDIAN_PANEL,
        border: `1px solid ${cc}88`,
        borderRadius: 14, padding: 20, boxShadow: SHADOW_FLOAT,
      }}>
        {/* The face — designed, since no art exists yet */}
        <div className={glowClass} style={{
          position: "relative", width: "100%", aspectRatio: "2/3",
          borderRadius: 10, overflow: "hidden", marginBottom: 12,
          border: `1px solid ${cc}66`, borderTop: `3px solid ${cc}`,
          background: `radial-gradient(120% 90% at 50% 0%, ${cc}26, transparent 60%), ${OBSIDIAN_SLAB}`,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          padding: 16, boxSizing: "border-box", textAlign: "center",
        }}>
          <FauxArt card={artCardOf(card)} mode="full" silhouette={silhouetteFor(card)} color={cc} species={card.species || null} />
          {foil && <HoloFoil tier={foil} radius={10} corners style={{ zIndex: 1 }} />}
        </div>

        {/* Foil preview row */}
        <div style={{ display: "flex", gap: 5, marginBottom: 12 }}>
          {FOILS.map(f => (
            <button key={String(f.id)} onClick={() => setFoil(f.id)} style={{
              flex: 1, padding: "6px 0", cursor: "pointer",
              background: foil === f.id ? `${(f.color || FX.ink)}22` : OBSIDIAN_SLAB,
              border: `1px solid ${foil === f.id ? (f.color || FX.ink) : GM.pageBorder}`,
              borderRadius: 6, color: f.color || GM.vellumDim,
              fontFamily: GM.font, fontSize: 9, fontWeight: 900, letterSpacing: 1,
            }}>{f.label}</button>
          ))}
        </div>

        <div style={{ fontSize: 15, fontWeight: 900, fontStyle: "italic", color: cc, marginBottom: 4, fontFamily: GM.font }}>
          {card.name}
        </div>
        <div style={{ fontSize: 10, color: GM.vellumFaint, letterSpacing: 2, marginBottom: 10, fontFamily: GM.font }}>
          {card.species ? `${card.species.toUpperCase()} · ` : ""}{(TYPE_LABELS[card.type] || card.type).toUpperCase()}
          {card.level ? ` · ${card.type === "Escendant" ? LEVEL_WORDS[card.level].toUpperCase() : "LVL " + card.level}` : ""}
          {card.khaoCost != null ? ` · ${card.khaoCost} KHAO` : ""}
          {card.animaCost != null ? ` · ${card.animaCost} ANIMA` : ""}
        </div>
        {(card.type === "Escendant" || card.type === "Escling") && (
          <div style={{ fontSize: 11, color: GM.vellumDim, marginBottom: 10, fontFamily: GM.font }}>
            ⚔ {card.lyt} Lyt{card.lyf != null ? ` · ⛨ ${card.lyf} Lyf` : ""} · deck limit {card.deckLimit ?? "—"}
          </div>
        )}

        {card.effect && (<>
          <div style={{
            fontSize: 8, letterSpacing: 2, color: FX.bright, fontFamily: GM.font,
            background: FX.wash, border: `1px solid ${FX.ink}55`, borderRadius: 3,
            padding: "3px 8px", display: "inline-block", marginBottom: 6,
          }}>✦ HYPOTHETICAL — NO RULES IMPLEMENTED</div>
          <div style={{ fontSize: 11, color: GM.vellumDim, lineHeight: 1.8, fontStyle: "italic", marginBottom: 12, fontFamily: GM.font }}>
            {card.effect}
          </div>
        </>)}
        {card.flavorText && (
          <div style={{
            fontSize: 11, color: GM.vellumDim, lineHeight: 1.85, fontFamily: GM.fontBody,
            borderTop: `1px solid ${GM.pageBorder}`, paddingTop: 10,
          }}>
            {card.flavorText}
          </div>
        )}

        {/* An Escendant's whole kit, right on its page */}
        {(impera.length > 0 || uhlti) && (
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${GM.pageBorder}` }}>
            <div style={{ fontSize: 9, letterSpacing: 2.5, color: GM.vellumFaint, marginBottom: 6, fontFamily: GM.font }}>
              ❧ ITS KIT (ALL HYPOTHETICAL)
            </div>
            {[...impera, ...(uhlti ? [uhlti] : [])].map(k => (
              <div key={k.id} style={{ fontSize: 10, color: GM.vellumDim, lineHeight: 1.7, marginBottom: 6, fontFamily: GM.font }}>
                <span style={{ color: cc, fontStyle: "italic" }}>{TYPE_ICONS[k.type]} {k.name}</span>
                {" — "}{k.effect}
              </div>
            ))}
          </div>
        )}

        <button onClick={onClose} style={{
          width: "100%", marginTop: 14, padding: "9px",
          background: "transparent", border: `1px solid ${GM.inkBrown}66`,
          borderRadius: 2, cursor: "pointer", color: GM.vellumFaint,
          fontFamily: GM.font, fontSize: 11, letterSpacing: 1,
        }}>✕ CLOSE</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN FAUXDEX COMPONENT
// ─────────────────────────────────────────────────────────────
const TABS = [
  { id: "cards",   label: "◆ Cards"   },
  { id: "decks",   label: "✦ Decks"   },
  { id: "schools", label: "◈ Schools" },
];

export default function Fauxdex({ onBack }) {
  const stage = useStageRect();
  const [activeTab, setActiveTab] = useState("cards");
  const [filterType, setFilterType] = useState("All");
  const [filterClass, setFilterClass] = useState("All");
  const [foil, setFoil] = useState(null); // grid-wide foil preview
  const [search, setSearch] = useState("");
  const [detailCard, setDetailCard] = useState(null);

  useModalKeys(detailCard ? () => setDetailCard(null) : onBack);

  const displayCards = useMemo(() => {
    return ALL_EXPERIMENTAL_CARDS.filter(card => {
      if (filterType !== "All" && card.type !== filterType) return false;
      if (filterClass !== "All" && classOf(card) !== filterClass) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!card.name.toLowerCase().includes(q) &&
            !card.species?.toLowerCase().includes(q) &&
            !card.effect?.toLowerCase().includes(q) &&
            !card.flavorText?.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [filterType, filterClass, search]);

  const btnStyle = (active, color = FX.ink) => ({
    padding: "5px 12px",
    background: active
      ? `linear-gradient(135deg, ${color}26, transparent 60%), ${OBSIDIAN_SLAB_LIT}`
      : OBSIDIAN_SLAB,
    border: `1px solid ${active ? color : GM.pageBorder}`,
    borderRadius: 8, cursor: "pointer",
    color: active ? color : GM.vellumDim,
    fontFamily: GM.font, fontSize: 10, fontWeight: 900,
    whiteSpace: "nowrap", transition: "all 0.15s",
    boxShadow: active
      ? `inset 0 1px 0 ${color}44, 0 0 10px ${color}33`
      : "inset 0 1px 0 rgba(226,232,244,0.1), 0 2px 6px rgba(0,0,0,0.45)",
    textShadow: TEXT_SET,
  });

  return (
    <div style={{
      position: "fixed", inset: 0, background: "transparent",
      display: "flex", alignItems: "center", justifyContent: "center",
      overflow: "hidden", fontFamily: GM.font, color: GM.vellum,
    }}>
    <div style={{
      position: "relative", width: stage.w, height: stage.h, flexShrink: 0,
      background: OBSIDIAN_BG,
      display: "flex", flexDirection: "column", overflow: "hidden",
    }}>
      <UnstableWorldBG />
      <StarDust count={22} style={{ position: "absolute" }} />
      <NoiseOverlay opacity={0.04} style={{ position: "absolute" }} />

      {detailCard && <FauxDetailModal card={detailCard} onClose={() => setDetailCard(null)} />}

      {/* ── HEADER ─────────────────────────────────────────── */}
      <div style={{ padding: "14px 20px 0", maxWidth: 600, margin: "0 auto", width: "100%", position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
          <button onClick={onBack} style={{
            background: OBSIDIAN_SLAB, border: "none",
            borderTop: "1px solid rgba(226,232,244,0.2)",
            clipPath: facetTB(7),
            boxShadow: "inset 0 1px 0 rgba(226,232,244,0.14), inset 0 -3px 6px rgba(0,0,0,0.4)",
            filter: "drop-shadow(0 2px 5px rgba(0,0,0,0.55))",
            color: GM.vellumDim, padding: "7px 15px", cursor: "pointer",
            fontFamily: GM.font, fontSize: 12, textShadow: TEXT_SET,
          }}>← Back</button>
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: 22, fontWeight: 900, fontStyle: "italic",
              color: FX.bright, textShadow: textGlow(FX.ink),
              letterSpacing: 3, fontFamily: GM.font,
            }}>✦ The Fauxdex</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 16, fontWeight: 900, color: FX.bright, fontFamily: GM.fontUI, textShadow: textGlow(FX.ink) }}>
              {ALL_EXPERIMENTAL_CARDS.length}
            </div>
            <div style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 2, fontFamily: GM.font }}>IMAGINED</div>
          </div>
        </div>
        <div style={{
          fontSize: 9.5, color: GM.vellumFaint, fontStyle: "italic",
          letterSpacing: 1, lineHeight: 1.6, marginBottom: 12,
          borderLeft: `2px solid ${FX.ink}66`, paddingLeft: 10,
        }}>
          Everything here is hypothesis. Nothing can be owned, bought, or played —
          a card leaves this hall only when it is canonized, and that is its blessing.
        </div>

        {/* Tab nav */}
        <div style={{ display: "flex", gap: 5, overflowX: "auto", paddingBottom: 4, marginBottom: 2 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
              padding: "8px 15px", cursor: "pointer",
              whiteSpace: "nowrap", transition: "all 0.18s",
              fontFamily: GM.font, fontSize: 10, letterSpacing: 1, fontWeight: 900,
              clipPath: facetTB(7), border: "none",
              borderTop: activeTab === t.id ? `1px solid ${FX.bright}` : "1px solid transparent",
              background: activeTab === t.id ? OBSIDIAN_SLAB_LIT : OBSIDIAN_SLAB,
              color: activeTab === t.id ? FX.bright : GM.vellumFaint,
              boxShadow: activeTab === t.id
                ? `inset 0 1px 0 ${FX.bright}66, inset 0 0 18px ${FX.ink}30`
                : "inset 0 1px 0 rgba(226,232,244,0.12), inset 0 -4px 8px rgba(0,0,0,0.4)",
              filter: activeTab === t.id ? `drop-shadow(0 0 8px ${FX.ink}40)` : "drop-shadow(0 2px 5px rgba(0,0,0,0.5))",
              textShadow: activeTab === t.id ? textGlow(FX.ink) : TEXT_SET,
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* ── TAB CONTENT ────────────────────────────────────── */}
      <div style={{
        flex: 1, overflowY: "auto", padding: "12px 20px 40px",
        maxWidth: 600, margin: "0 auto", width: "100%",
        boxSizing: "border-box", position: "relative", zIndex: 1,
      }}>

        {/* ══ CARDS TAB ══ */}
        {activeTab === "cards" && (<>
          <input
            placeholder="🔍 Search the hypothesis..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{
              width: "100%", background: OBSIDIAN_RECESS,
              border: `1px solid ${GM.pageBorder}`, borderRadius: 10,
              boxShadow: SHADOW_RECESS, color: GM.vellum, padding: "10px 14px",
              fontFamily: GM.font, fontSize: 12, outline: "none",
              marginBottom: 10, boxSizing: "border-box", caretColor: FX.ink,
            }}
          />
          <div style={{ display: "flex", gap: 5, overflowX: "auto", marginBottom: 8, paddingBottom: 4 }}>
            {TYPE_OPTIONS.map(t => (
              <button key={t} onClick={() => setFilterType(t)} style={btnStyle(filterType === t)}>
                {TYPE_ICONS[t] || ""} {TYPE_LABELS[t] || t}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 5, overflowX: "auto", marginBottom: 8, paddingBottom: 4 }}>
            {CLASS_OPTIONS.map(c => (
              <button key={c} onClick={() => setFilterClass(c)}
                style={btnStyle(filterClass === c, c === "All" ? FX.ink : classColor(c))}>
                {c}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 5, marginBottom: 14 }}>
            {FOILS.map(f => (
              <button key={String(f.id)} onClick={() => setFoil(f.id)}
                style={btnStyle(foil === f.id, f.color || FX.ink)}>
                {f.label}
              </button>
            ))}
          </div>
          <div style={{
            fontSize: 9, color: GM.vellumFaint, fontFamily: GM.font,
            letterSpacing: 3, marginBottom: 12,
            borderBottom: `1px solid ${GM.ruling}`, paddingBottom: 8,
          }}>
            {displayCards.length} CARDS IMAGINED
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {displayCards.map(card => (
              <FauxTile key={card.id} card={card} foil={foil} onClick={setDetailCard} />
            ))}
          </div>
          {displayCards.length === 0 && (
            <div style={{ textAlign: "center", padding: "60px 20px", color: GM.vellumFaint, fontStyle: "italic", fontFamily: GM.font }}>
              <div style={{ fontSize: 44, marginBottom: 16, opacity: 0.15 }}>✦</div>
              <div style={{ fontSize: 13, color: GM.vellumDim, marginBottom: 8 }}>Nothing imagined matches that.</div>
              <div style={{ fontSize: 11 }}>Try adjusting your filters.</div>
            </div>
          )}
        </>)}

        {/* ══ DECKS TAB ══ */}
        {activeTab === "decks" && (<>
          <div style={{
            marginBottom: 16, padding: "10px 16px",
            background: OBSIDIAN_SLAB, border: `1px solid ${GM.pageBorder}`,
            borderLeft: `3px solid ${FX.ink}`,
            borderRadius: 8, fontSize: 11, color: GM.vellumDim,
            fontStyle: "italic", lineHeight: 1.7, boxShadow: SHADOW_RAISED, textShadow: TEXT_SET,
          }}>
            ✦ {EX_STARTER_DECKS.length} hypothetical starter decks — one per new line,
            each built on the same core Tomes as the 12 canon starters. Recipes only;
            none can be forged.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {EX_STARTER_DECKS.map(deck => {
              const total = Object.values(deck.grimoire).reduce((a, b) => a + b, 0)
                + Object.values(deck.luxmoire).reduce((a, b) => a + b, 0)
                + deck.esclings.length;
              return (
                <div key={deck.id} style={{
                  padding: "12px 16px", background: OBSIDIAN_SLAB,
                  clipPath: facetTB(9), border: "none",
                  borderTop: `2px solid ${deck.color || FX.ink}`,
                  boxShadow: "inset 0 1px 0 rgba(226,232,244,0.12), inset 0 -4px 9px rgba(0,0,0,0.45)",
                  filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.5))",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                    <div style={{ fontSize: 13, fontStyle: "italic", fontWeight: 900, color: GM.vellum, fontFamily: GM.font }}>
                      {deck.name}
                    </div>
                    <div style={{ fontSize: 8, letterSpacing: 2, color: deck.color || FX.ink, fontFamily: GM.font, whiteSpace: "nowrap" }}>
                      {(deck.ilk || "").toUpperCase()} · {total} CARDS
                    </div>
                  </div>
                  <div style={{ fontSize: 10, color: GM.vellumDim, lineHeight: 1.6, marginTop: 6, fontFamily: GM.font }}>
                    {deck.description}
                  </div>
                </div>
              );
            })}
          </div>
        </>)}

        {/* ══ SCHOOLS TAB ══ */}
        {activeTab === "schools" && (<>
          <div style={{
            marginBottom: 16, padding: "10px 16px",
            background: OBSIDIAN_SLAB, border: `1px solid ${GM.pageBorder}`,
            borderLeft: `3px solid ${FX.ink}`,
            borderRadius: 8, fontSize: 11, color: GM.vellumDim,
            fontStyle: "italic", lineHeight: 1.7, boxShadow: SHADOW_RAISED, textShadow: TEXT_SET,
          }}>
            ◈ Eight new Tome Schools — doubling the canon eight, no more, no less.
            Each has a mechanical law no other School shares.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {EXPERIMENTAL_TOME_SCHOOLS.map(s => (
              <div key={s.id} style={{
                padding: "12px 16px", background: OBSIDIAN_SLAB,
                border: `1px solid ${GM.pageBorder}`,
                borderLeft: `3px solid ${classColor(s.id)}`,
                borderRadius: 8, boxShadow: SHADOW_RAISED,
              }}>
                <div style={{ fontSize: 13, fontWeight: 900, fontStyle: "italic", color: classColor(s.id), fontFamily: GM.font, marginBottom: 4 }}>
                  {s.label}
                </div>
                <div style={{ fontSize: 10.5, color: GM.vellumDim, lineHeight: 1.7, fontFamily: GM.font }}>
                  {s.description}
                </div>
              </div>
            ))}
          </div>
        </>)}

      </div>
    </div>
    </div>
  );
}
